import type { ReactNode } from 'react';
import { UMButton, UMFormStyles, UMIcon } from '../../urbanmap/_components';
import { PlanLogo } from '../proposition-c/ui';
import { fmt } from './domain';
import { PC, UMR, caption } from './tokens';
import type { ProjectSummary } from './types';

interface Props {
  projects: ProjectSummary[] | undefined;
  loading: boolean;
  error: boolean;
  onCreate: () => void;
  onOpen: (id: string) => void;
}

const COLS = ['Nom', 'Périmètre', 'Bâtiments', 'Copie UrbanMap', 'Propriétaire', 'Dernière modification'];
const cell = { padding: '12px 16px', borderBottom: `1px solid ${UMR.borderSubtle}`, fontSize: 14, lineHeight: '20px', textAlign: 'left' as const };

function State({ icon, title, children }: { icon: string; title: string; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '64px 24px', border: `1px dashed ${UMR.border}`, color: UMR.textSecondary, textAlign: 'center' }}>
      <UMIcon name={icon} size={40} color={UMR.textDisabled} />
      <div style={{ fontSize: 18, fontWeight: 700, color: UMR.textStrong }}>{title}</div>
      {children}
    </div>
  );
}

/** Accueil Plan : liste des projets réseau de chaleur + « Nouveau projet ». */
export function HomePage({ projects, loading, error, onCreate, onOpen }: Props) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <UMFormStyles />
      <header style={{ height: PC.headerHeight, minHeight: PC.headerHeight, boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 16, padding: '0 24px', borderTop: `3px solid ${PC.accent}`, borderBottom: `1px solid ${UMR.border}` }}>
        <PlanLogo size={36} />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 15, fontWeight: 700, lineHeight: '20px', color: UMR.textStrong }}>Plan · Projets réseaux de chaleur</span>
          <span style={caption}>Bâtiments copiés depuis UrbanMap · synchronisation manuelle</span>
        </div>
      </header>

      <main style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', background: UMR.grey100 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, lineHeight: '32px', fontWeight: 700, color: UMR.textStrong }}>Mes projets</h1>
              <div style={caption}>{projects ? `${projects.length} projet${projects.length > 1 ? 's' : ''}` : '\u00a0'}</div>
            </div>
            <UMButton icon="add" onClick={onCreate}>Nouveau projet</UMButton>
          </div>

          {loading && <State icon="hourglass_top" title="Chargement des projets…" />}
          {error && <State icon="error" title="Impossible de charger les projets">Réessayez dans quelques instants.</State>}
          {projects && projects.length === 0 && (
            <State icon="folder_off" title="Aucun projet">
              <span>Créez un premier projet : choisissez un périmètre, les bâtiments UrbanMap y seront copiés.</span>
              <UMButton icon="add" onClick={onCreate}>Nouveau projet</UMButton>
            </State>
          )}
          {projects && projects.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', background: UMR.white, border: `1px solid ${UMR.border}` }}>
              <thead>
                <tr style={{ background: UMR.white }}>
                  {COLS.map((c, i) => (
                    <th key={c} style={{ ...cell, fontSize: 12, fontWeight: 700, color: UMR.textLabel, borderBottom: `1px solid ${UMR.border}`, textAlign: i === 2 ? 'right' : 'left' }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr
                    key={p.id}
                    className="um-hover-grey100"
                    tabIndex={0}
                    onClick={() => onOpen(p.id)}
                    onKeyDown={(e) => e.key === 'Enter' && onOpen(p.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ ...cell, fontWeight: 700, color: UMR.blueLink }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <UMIcon name="folder" size={20} color={PC.accent} />
                        {p.name}
                      </span>
                    </td>
                    <td style={cell}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <UMIcon name={p.perimeterLabel === 'Contour dessiné' ? 'draw' : 'location_city'} size={16} color={UMR.textSecondary} />
                        {p.perimeterLabel}
                      </span>
                    </td>
                    <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(p.buildingCount)}</td>
                    <td style={cell}>{p.snapshotDate}</td>
                    <td style={cell}>{p.owner}</td>
                    <td style={{ ...cell, color: UMR.textSecondary }}>{p.updatedAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
